/**
 * VaultGraphCanvas — the XYFlow-driven graph component shared between
 * the embedded pane (inside ObsidianVaultWindow) and the popout
 * fullscreen Vault Graph window.
 *
 * Behavior per Q32:
 *   - Click node → onOpen(path, 'pane-a')
 *   - Shift / Cmd / Ctrl click → onOpen(path, 'pane-b')
 *   - Right-click → onContextMenu({path, x, y})
 *
 * Layout: ELK runs once on input change. Two modes: layered + force.
 * Tag and folder filters narrow the visible node + edge set without
 * recomputing the layout (so positions stay stable across filter
 * toggles).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  buildVaultGraphInputs,
  computeVaultGraphLayout,
  type LaidOutGraph,
  type LayoutMode,
} from '../../lib/vault/graphLayout';
import type { ParsedNote } from '../../lib/vault/parse';
import { testIds } from '../../lib/testIds';

interface NodeData extends Record<string, unknown> {
  label: string;
  isActive: boolean;
  weight: number;
}

function VaultNodeView({ data, selected }: NodeProps<Node<NodeData>>) {
  const isActive = data.isActive;
  return (
    <div
      style={{
        padding: '4px 8px',
        border: `1px solid ${isActive ? '#ffd966' : '#5b6ee3'}`,
        background: isActive ? '#fff7d6' : selected ? '#dfe6ff' : '#ffffff',
        color: '#101010',
        borderRadius: 2,
        fontSize: 11,
        fontFamily: "var(--xp-font-body), sans-serif",
        boxShadow: '1px 1px 0 #00000022',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        maxWidth: 200,
        position: 'relative',
      }}
      title={data.label}
    >
      {/* XYFlow v12 requires explicit Handle components on custom nodes — */}
      {/* without them, edges without a sourceHandle/targetHandle log */}
      {/* "Couldn't create edge for source handle id: 'null'" warnings. */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: 'transparent', border: 'none', width: 1, height: 1 }}
        isConnectable={false}
      />
      {data.label}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: 'transparent', border: 'none', width: 1, height: 1 }}
        isConnectable={false}
      />
    </div>
  );
}

const nodeTypes = { vaultNode: VaultNodeView };
// Module-scope so XYFlow's referential-stability check is happy.
const PRO_OPTIONS = { hideAttribution: true };
const DEFAULT_EDGE_OPTIONS = { style: { stroke: '#5b6ee3', strokeWidth: 1 } };

export interface VaultGraphCanvasProps {
  notes: ParsedNote[];
  activePath: string | null;
  onOpen: (path: string, modifier: 'pane-a' | 'pane-b') => void;
  /** Optional initial mode; defaults to 'force'. */
  initialMode?: LayoutMode;
  /** Hide built-in controls (zoom / fit) — popout window keeps them; embedded pane hides them. */
  showControls?: boolean;
  /** Optional context-menu handler. */
  onContextMenu?: (path: string, x: number, y: number) => void;
}

export function VaultGraphCanvas({
  notes,
  activePath,
  onOpen,
  initialMode = 'force',
  showControls = true,
  onContextMenu,
}: VaultGraphCanvasProps) {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(initialMode);
  const [tagFilter, setTagFilter] = useState<string | 'all'>('all');
  const [folderFilter, setFolderFilter] = useState<string | 'all'>('all');
  const [laid, setLaid] = useState<LaidOutGraph | null>(null);
  const mountedRef = useRef(true);

  // Tag + folder choices derived from the vault.
  const { availableTags, availableFolders } = useMemo(() => {
    const tags = new Set<string>();
    const folders = new Set<string>();
    for (const n of notes) {
      for (const t of n.tags) tags.add(t);
      const slash = n.path.indexOf('/');
      if (slash > 0) folders.add(n.path.slice(0, slash));
    }
    return {
      availableTags: [...tags].sort(),
      availableFolders: [...folders].sort(),
    };
  }, [notes]);

  // Apply filters before layout. We compute layout on the filtered set
  // so positions tighten when filters narrow the graph; the trade-off
  // is positions shift, but that's the expected affordance for a
  // filter pass.
  const filteredNotes = useMemo(() => {
    return notes.filter((n) => {
      if (tagFilter !== 'all' && !n.tags.includes(tagFilter)) return false;
      if (folderFilter !== 'all' && !n.path.startsWith(`${folderFilter}/`))
        return false;
      return true;
    });
  }, [notes, tagFilter, folderFilter]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ELK layout — async; cancelled on input change via the mounted ref.
  useEffect(() => {
    const { nodes: gNodes, edges: gEdges } = buildVaultGraphInputs(filteredNotes);
    let cancelled = false;
    (async () => {
      try {
        const result = await computeVaultGraphLayout(gNodes, gEdges, layoutMode);
        if (!cancelled && mountedRef.current) setLaid(result);
      } catch (err) {
        // ELK can throw on bizarre input; surface as null so the
        // surface renders an empty canvas rather than crashing.
        console.warn('[vault graph] layout failed', err);
        if (!cancelled && mountedRef.current) setLaid(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filteredNotes, layoutMode]);

  const reactFlowNodes: Node<NodeData>[] = useMemo(() => {
    if (!laid) return [];
    return laid.nodes.map((n) => ({
      id: n.id,
      type: 'vaultNode',
      position: { x: n.x, y: n.y },
      data: {
        label: n.label,
        isActive: n.id === activePath,
        weight: n.weight,
      },
      draggable: true,
      selectable: true,
    }));
  }, [laid, activePath]);

  const reactFlowEdges: Edge[] = useMemo(() => {
    if (!laid) return [];
    return laid.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      animated: false,
      style: {
        stroke: e.kind === 'embed' ? '#8a5bc4' : '#5b6ee3',
        strokeWidth: 1,
        opacity: 0.7,
      },
    }));
  }, [laid]);

  const onNodeClick = useCallback<NodeMouseHandler<Node<NodeData>>>(
    (event, node) => {
      const splitMod = event.metaKey || event.ctrlKey || event.shiftKey;
      onOpen(node.id, splitMod ? 'pane-b' : 'pane-a');
    },
    [onOpen],
  );

  const onNodeContextMenu = useCallback<NodeMouseHandler<Node<NodeData>>>(
    (event, node) => {
      if (!onContextMenu) return;
      event.preventDefault();
      onContextMenu(node.id, event.clientX, event.clientY);
    },
    [onContextMenu],
  );

  // Memoize fitViewOptions so XYFlow doesn't fire the "new nodeTypes/edgeTypes
  // object" warning on every render. The anchor changes only when activePath
  // or the laid-out node set changes; keep the dependency tight.
  const fitViewOptions = useMemo(() => {
    const anchor =
      reactFlowNodes.find((n) => n.id === activePath) ?? reactFlowNodes[0];
    return {
      nodes: anchor ? [{ id: anchor.id }] : undefined,
      padding: 0.6,
      maxZoom: 1.8,
      minZoom: 0.8,
    };
  }, [reactFlowNodes, activePath]);

  return (
    <div
      data-testid={testIds.obsidianVault.graphPane}
      style={{ height: '100%', position: 'relative', background: '#101428' }}
    >
      <div
        style={{
          position: 'absolute',
          top: 4,
          left: 4,
          right: 4,
          display: 'flex',
          gap: 6,
          zIndex: 10,
          fontSize: 11,
          color: '#cfd6f5',
          alignItems: 'center',
        }}
      >
        <select
          data-testid={testIds.vaultGraph.layoutToggle}
          value={layoutMode}
          onChange={(e) => setLayoutMode(e.target.value as LayoutMode)}
          style={{ padding: '1px 4px', background: 'white', color: 'black' }}
        >
          <option value="force">Force</option>
          <option value="layered">Layered</option>
        </select>
        <select
          data-testid={testIds.vaultGraph.tagFilter}
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          style={{ padding: '1px 4px', background: 'white', color: 'black' }}
        >
          <option value="all">All tags</option>
          {availableTags.map((t) => (
            <option key={t} value={t}>
              #{t}
            </option>
          ))}
        </select>
        <select
          data-testid={testIds.vaultGraph.folderFilter}
          value={folderFilter}
          onChange={(e) => setFolderFilter(e.target.value)}
          style={{ padding: '1px 4px', background: 'white', color: 'black' }}
        >
          <option value="all">All folders</option>
          {availableFolders.map((f) => (
            <option key={f} value={f}>
              {f}/
            </option>
          ))}
        </select>
        <span style={{ flex: 1 }} />
        <span style={{ color: '#7e8bb9' }}>
          {reactFlowNodes.length} nodes · {reactFlowEdges.length} links
        </span>
      </div>
      <ReactFlow
        data-testid={testIds.vaultGraph.canvas}
        nodes={reactFlowNodes}
        edges={reactFlowEdges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onNodeContextMenu={onNodeContextMenu}
        fitView
        // Memoized: centers on activePath (or the first node) and zooms in
        // ~3x tighter than a fit-all view.
        fitViewOptions={fitViewOptions}
        proOptions={PRO_OPTIONS}
        minZoom={0.2}
        maxZoom={3}
        defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
        style={{ background: '#101428' }}
      >
        <Background gap={20} color="#1d2547" />
        {showControls && <Controls position="bottom-right" />}
      </ReactFlow>
    </div>
  );
}
